import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress, 
  FormControl,
  Grid, 
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField, 
  Typography 
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import { db } from "../../../../Config/firebase.ts";
import { collection, addDoc } from "firebase/firestore";
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2.ts';

interface FormData {
  name: string;
  phone: string;
  email: string;
  source: string;
  status: string;
  notes: string;
  originatorId: string;
}

export default function AddLeadModal() {
  const open = useAppSelector((state: RootState) => state.ModalSlice.AddLeadModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const userData = useAppSelector((state: RootState) => state.userSliceV2.data);
  const userId = userData?.uid || '';
  const ServiceProviderID = useAppSelector(selectEffectiveUID) 
  const userEmail = userData?.email || '';
  const userDisplayName = userData?.display_name || '';
  const dispatch = useAppDispatch();

  const defaultOriginatorId = userDisplayName || userEmail || 'Unknown';

  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    source: 'Website',
    status: 'hot',
    notes: '',
    originatorId: defaultOriginatorId
  });

  const handleClose = () => {
    dispatch(closeModal("AddLeadModalOpen"));
    // Reset form data when closing
    setFormData({
      name: '',
      phone: '',
      email: '',
      source: 'Website',
      status: 'hot',
      notes: '',
      originatorId: defaultOriginatorId
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData({...formData, [name]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone number format (10 digits)
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    // Validate email only if provided (email is optional)
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }

    if (!userId) {
      toast.error("User not authenticated");
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      const timestamp = Date.now();
      const createdBy = userDisplayName || userEmail || 'Unknown';
      
      // Add lead to Firestore
      const leadsRef = collection(db, `ServiceProviders/${ServiceProviderID}/Leads`);
      
      const leadData = {
        ...formData,
        lastContact: new Date().toISOString().split('T')[0], // Today's date
        createdAt: timestamp,
        createdBy: createdBy,
        // Initialize notesHistory and updateHistory arrays
        notesHistory: [],
        updateHistory: []
      };
      
      // Add the document to Firestore
      await addDoc(leadsRef, leadData);
      
      toast.success("Lead added successfully!");
      handleClose();
    } catch (error) {
      console.error("Error adding lead:", error);
      toast.error("Failed to add lead. Please try again.");
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '10px',
        },
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <Typography variant="h6">Add New Lead</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10-digit number"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel id="lead-source-label">Source</InputLabel>
                <Select
                  labelId="lead-source-label"
                  id="lead-source"
                  name="source"
                  value={formData.source}
                  label="Source"
                  onChange={handleSelectChange as any}
                >
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Email Campaign">Email Campaign</MenuItem>
                  <MenuItem value="Trade Show">Trade Show</MenuItem>
                  <MenuItem value="Cold Call">Cold Call</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel id="lead-status-label">Status</InputLabel>
                <Select
                  labelId="lead-status-label"
                  id="lead-status"
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange as any}
                >
                  <MenuItem value="hot">Hot</MenuItem>
                  <MenuItem value="cold">Cold</MenuItem>
                  <MenuItem value="obsolete">Obsolete</MenuItem>
                  <MenuItem value="win">Win</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Originator ID"
                name="originatorId"
                value={formData.originatorId}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={3}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : null}
              >
                {isLoading ? 'Adding...' : 'Add Lead'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
} 